import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Order Document (matches backend OrderDocument : BaseDocument) ---

export interface Order {
  id: string;
  tenant_id: string;
  quote_id: string;
  customer_id: string;
  contact_id: string;
  order_number: string;
  title: string;
  description: string;
  status: string;
  order_date: string | null;
  subtotal: number;
  discount_percentage: number;
  discount_amount: number;
  tax_percentage: number;
  tax_amount: number;
  total: number;
  currency: string;
  shipping_address: string;
  payment_terms: string;
  notes: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface OrderLineItem {
  id: string;
  tenant_id: string;
  order_id: string;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_percentage: number;
  line_total: number;
  sort_order: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs ---

export interface CreateOrderRequest {
  customer_id: string;
  contact_id?: string;
  quote_id?: string;
  order_number?: string;
  title: string;
  description?: string;
  order_date?: string;
  discount_percentage?: number;
  discount_amount?: number;
  tax_percentage?: number;
  currency?: string;
  shipping_address?: string;
  payment_terms?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateOrderRequest {
  customer_id: string;
  contact_id?: string;
  quote_id?: string;
  order_number?: string;
  title: string;
  description?: string;
  status?: string;
  order_date?: string;
  discount_percentage?: number;
  discount_amount?: number;
  tax_percentage?: number;
  currency?: string;
  shipping_address?: string;
  payment_terms?: string;
  notes?: string;
  tags?: string[];
}

export interface CreateOrderLineItemRequest {
  product_name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  sort_order?: number;
}

export interface UpdateOrderLineItemRequest {
  product_name: string;
  description?: string;
  quantity: number;
  unit_price: number;
  discount_percentage?: number;
  sort_order?: number;
}

// --- Filter params for order list ---

export interface OrderFilters {
  customer_id?: string;
  contact_id?: string;
  quote_id?: string;
  status?: string;
  currency?: string;
}

// --- Order API functions ---

const BASE = '/orders';

export async function listOrders(
  params: SearchParams,
  filters?: OrderFilters
): Promise<PagedResponse<Order>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.customer_id) query.customer_id = filters.customer_id;
  if (filters?.contact_id) query.contact_id = filters.contact_id;
  if (filters?.quote_id) query.quote_id = filters.quote_id;
  if (filters?.status) query.status = filters.status;
  if (filters?.currency) query.currency = filters.currency;

  const response = await apiGet<PagedResponse<Order>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getOrder(id: string): Promise<Order> {
  const response = await apiGet<Order>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Order not found');
  }
  return response.data;
}

export async function createOrder(data: CreateOrderRequest): Promise<Order> {
  const response = await apiPost<Order>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create order');
  }
  return response.data;
}

export async function updateOrder(id: string, data: UpdateOrderRequest): Promise<Order> {
  const response = await apiPut<Order>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update order');
  }
  return response.data;
}

export async function deleteOrder(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}

export async function createOrderFromQuote(quoteId: string): Promise<Order> {
  const response = await apiPost<Order>(`${BASE}/from-quote/${quoteId}`, {});
  if (!response.data) {
    throw new Error(response.message || 'Failed to create order from quote');
  }
  return response.data;
}

// --- Line Item API functions ---

export async function listOrderLineItems(orderId: string): Promise<OrderLineItem[]> {
  const response = await apiGet<OrderLineItem[]>(`${BASE}/${orderId}/line-items`);
  if (!response.data) {
    return [];
  }
  return response.data;
}

export async function createOrderLineItem(
  orderId: string, data: CreateOrderLineItemRequest
): Promise<OrderLineItem> {
  const response = await apiPost<OrderLineItem>(`${BASE}/${orderId}/line-items`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create line item');
  }
  return response.data;
}

export async function updateOrderLineItem(
  orderId: string, itemId: string, data: UpdateOrderLineItemRequest
): Promise<OrderLineItem> {
  const response = await apiPut<OrderLineItem>(`${BASE}/${orderId}/line-items/${itemId}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update line item');
  }
  return response.data;
}

export async function deleteOrderLineItem(
  orderId: string, itemId: string
): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${orderId}/line-items/${itemId}`);
}
